import axios from "axios";
import { normalizeExplorerApiBaseUrl } from "./apiBaseUrl";
import { getConfig } from "./runtimeConfig";

const apiBase = normalizeExplorerApiBaseUrl(getConfig("BASE_API_URL")) || "";

// Border-gateway migration: under AUTH_MODE=gateway, identity travels via the shared session
// cookie (see context/GatewayAuthBridge.js), which the browser only attaches cross-origin when
// the request opts in -- axios's own `withCredentials`. Under the default auth0-spa mode this
// stays false (today's behavior): the API base URL and the SPA origin are different origins,
// and auth there is Bearer-token-only, so cookies were never needed on these requests.
const isGatewayAuthMode = getConfig("AUTH_MODE") === "gateway";

const apiClient = axios.create({
  baseURL: apiBase || undefined,
  withCredentials: isGatewayAuthMode,
});

/**
 * Auth0 access token for API calls (same audience as REACT_APP_AUTH0_AUDIENCE).
 * Use this for fetch() / EventSource paths that cannot use the axios interceptor.
 *
 * @param {{ maxRetries?: number }} [options] maxRetries>0 only for early calls (e.g. search/parameters SSE)
 * where Auth0’s cache may not be ready yet; axios uses default 0.
 */
export async function getApiAccessToken(options = {}) {
  let maxRetries = options.maxRetries ?? 0;
  // While Auth0 is hydrating, isAuthenticated is often still false — do not treat as "logged out"
  // yet; retry like an authenticated session so the first API/search call can get a Bearer token.
  if (
    typeof window !== "undefined" &&
    maxRetries === 0 &&
    window.__auth0IsLoading === true
  ) {
    maxRetries = 8;
  }
  const maxAttempts = 1 + maxRetries;

  if (typeof window === "undefined") {
    return null;
  }

  // Requests can run before AppContent’s first render assigns __getAccessTokenSilently.
  let getToken = window.__getAccessTokenSilently;
  if (!getToken) {
    const deadline = Date.now() + 2500;
    while (typeof window.__getAccessTokenSilently !== "function" && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 30));
    }
    getToken = window.__getAccessTokenSilently;
  }
  if (!getToken) {
    if (process.env.NODE_ENV === "development" && window.__isAuthenticated) {
      console.warn(
        "[api] No __getAccessTokenSilently — Auth0 AppContent may not have mounted yet."
      );
    }
    return null;
  }

  const audience = getConfig("AUTH0_AUDIENCE");
  const silentOpts =
    audience ? { authorizationParams: { audience } } : {};

  const tryOnce = async () => {
    try {
      const token = await getToken(silentOpts);
      return token || null;
    } catch (e) {
      return { error: e };
    }
  };

  let lastErr = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = await tryOnce();
    if (typeof result === "string" && result.length > 0) {
      return result;
    }
    if (result && result.error) {
      lastErr = result.error;
    }
    if (!window.__isAuthenticated) {
      const stillLoading =
        typeof window !== "undefined" && window.__auth0IsLoading === true;
      if (!stillLoading) {
        break;
      }
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, 40 + attempt * 40));
    }
  }

  if (lastErr) {
    const code = lastErr?.error || lastErr?.code;
    console.warn(
      "[api] getAccessTokenSilently failed" +
        (maxRetries ? " after retries" : "") +
        ":",
      code || lastErr?.message || lastErr
    );
  } else if (
    process.env.NODE_ENV === "development" &&
    window.__isAuthenticated &&
    !audience
  ) {
    console.warn(
      "[api] No access token: set REACT_APP_AUTH0_AUDIENCE to your Auth0 API identifier (same as backend AUTH0_AUDIENCE)."
    );
  }

  return null;
}

/** Dedupe concurrent force-refreshes (e.g. many parallel 401s). */
let forceRefreshInFlight = null;

/**
 * Ask Auth0 for a new access token, bypassing the SPA cache (after API 401).
 * @returns {Promise<string|null>}
 */
export async function forceRefreshAccessToken() {
  if (typeof window === "undefined") {
    return null;
  }
  const getToken = window.__getAccessTokenSilently;
  if (typeof getToken !== "function") {
    return null;
  }
  if (!forceRefreshInFlight) {
    forceRefreshInFlight = (async () => {
      try {
        const token = await getToken({
          cacheMode: "off",
        });
        return typeof token === "string" && token.length > 0 ? token : null;
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "[api] forceRefreshAccessToken:",
            e?.error || e?.message || e
          );
        }
        return null;
      } finally {
        forceRefreshInFlight = null;
      }
    })();
  }
  return forceRefreshInFlight;
}

async function recoverFromInvalidApiSession() {
  const fn = window.__recoverFromInvalidSession;
  if (typeof fn === "function") {
    await fn();
  }
}

const authAppUrl = (getConfig("AUTH_APP_URL") || "").replace(/\/+$/, "");
let sessionProbeInFlight = null;

// The gateway's own 401 carries no CORS headers, so the browser hides it and the call fails with
// no status. The auth app's introspect does send them, so it can still tell an ended session from
// an outage.
async function recoverIfGatewaySessionEnded() {
  const path = window.location.pathname;
  if (!isGatewayAuthMode || !authAppUrl || path === "/login" || path === "/logout") {
    return;
  }
  sessionProbeInFlight ??= (async () => {
    try {
      const response = await fetch(`${authAppUrl}/session/introspect`, {
        credentials: "include",
      });
      if (response.status === 401) {
        await recoverFromInvalidApiSession();
      }
    } catch {
      /* auth app unreachable: a real outage, so the caller's original error stands */
    } finally {
      sessionProbeInFlight = null;
    }
  })();
  await sessionProbeInFlight;
}

/**
 * Browser `fetch()` with the same access-token behavior as the axios interceptor.
 * Use for streaming/SSE routes that cannot use `apiClient` (no streaming adapter parity).
 *
 * - Merges `Authorization: Bearer …` from {@link getApiAccessToken} (retries when authenticated).
 * - For same-origin URLs, sets `credentials: "include"` so `access_token` cookies from the
 *   server-side OAuth callback still reach `verify_token` if Bearer is absent.
 *
 * @param {RequestInfo} input
 * @param {RequestInit & { maxRetries?: number }} [init]
 *   Pass `maxRetries` like {@link getApiAccessToken} (default: 8 when authenticated, 0 otherwise —
 *   slightly higher than axios for early calls such as search/parameters).
 */
export async function fetchWithApiAuth(input, init = {}) {
  const { maxRetries: maxRetriesOverride, ...rest } = init;
  const maxRetries =
    maxRetriesOverride !== undefined
      ? maxRetriesOverride
      : typeof window !== "undefined" && window.__isAuthenticated
        ? 8
        : 0;

  const path =
    typeof window !== "undefined" ? window.location.pathname : "";

  const accessToken = await getApiAccessToken({ maxRetries });
  const headers = new Headers(rest.headers ?? {});
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  } else if (
    process.env.NODE_ENV === "development" &&
    typeof window !== "undefined" &&
    window.__isAuthenticated
  ) {
    console.warn(
      "[api] fetchWithApiAuth: no Bearer token while authenticated — check REACT_APP_AUTH0_AUDIENCE and Auth0 mount."
    );
  }

  let credentials = rest.credentials;
  if (credentials === undefined && typeof window !== "undefined") {
    if (isGatewayAuthMode) {
      // The API's origin (api.explorer.*) is cross-origin from the SPA's (explorer.*) even
      // though both share the session cookie's domain -- "same-origin" would silently drop the
      // cookie on every one of these calls, which is exactly the request shape SSE/EventSource
      // (search/parameters) uses.
      credentials = "include";
    } else {
      try {
        const urlStr =
          typeof input === "string"
            ? input
            : typeof Request !== "undefined" && input instanceof Request
              ? input.url
              : String(input);
        const resolved = new URL(urlStr, window.location.href);
        credentials =
          resolved.origin === window.location.origin ? "include" : "same-origin";
      } catch {
        credentials = "same-origin";
      }
    }
  }

  const doFetch = (token) => {
    const h = new Headers(rest.headers ?? {});
    if (token) {
      h.set("Authorization", `Bearer ${token}`);
    }
    return fetch(input, { ...rest, headers: h, credentials });
  };

  let response;
  try {
    response = await doFetch(accessToken);
  } catch (error) {
    await recoverIfGatewaySessionEnded();
    throw error;
  }

  if (response.status !== 401 || path === "/login" || path === "/logout") {
    return response;
  }

  if (!window.__isAuthenticated) {
    window.location.assign("/login");
    return response;
  }

  const fresh = await forceRefreshAccessToken();
  if (fresh) {
    response = await doFetch(fresh);
  }

  if (response.status === 401) {
    try {
      await recoverFromInvalidApiSession();
    } catch {
      /* logout navigates away */
    }
  }

  return response;
}

apiClient.interceptors.request.use(
  async (config) => {
    try {
      const getUser = window.__getAuthUser;
      // Auth0’s in-memory cache may lag isAuthenticated; retry when we expect a token.
      const accessToken = await getApiAccessToken({
        maxRetries: window.__isAuthenticated ? 6 : 0,
      });
      const userInfo = getUser ? await getUser() : null;
      const email = userInfo?.email ?? "anonymous";

      // Add user info to headers (Auth0 user may be undefined before login)
      config.headers.userInfo = {
        email,
      };

      // Only send analytics data if email is not from murmuration.org
      if (!email.endsWith("@murmuration.org")) {
        config.headers.sendAnalytics = true;
      } else {
        config.headers.sendAnalytics = false;
      }

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      } else if (
        process.env.NODE_ENV === "development" &&
        window.__isAuthenticated
      ) {
        console.warn(
          "[api] Request without Bearer token while authenticated:",
          config.method,
          config.url
        );
      }

      return config;
    } catch (error) {
      console.error("Error in request interceptor:", error);
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    if (!error.response && !axios.isCancel(error)) {
      await recoverIfGatewaySessionEnded();
    }
    if (status !== 401) {
      return Promise.reject(error);
    }

    const path = window.location.pathname;
    if (path === "/login" || path === "/logout") {
      return Promise.reject(error);
    }

    const config = error.config;
    if (!config) {
      return Promise.reject(error);
    }

    if (!window.__isAuthenticated) {
      window.location.assign("/login");
      return Promise.reject(error);
    }

    if (config._retryAfter401Refresh) {
      try {
        await recoverFromInvalidApiSession();
      } catch {
        /* logout may redirect */
      }
      return Promise.reject(error);
    }

    const token = await forceRefreshAccessToken();
    if (token) {
      config._retryAfter401Refresh = true;
      if (config.headers) {
        if (typeof config.headers.set === "function") {
          config.headers.set("Authorization", `Bearer ${token}`);
        } else {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return apiClient.request(config);
    }

    try {
      await recoverFromInvalidApiSession();
    } catch {
      /* */
    }
    return Promise.reject(error);
  }
);

export default apiClient;
